import React, { useState, useRef, useEffect } from 'react';
import { ShoppingBag, User, Heart, Search, Menu, X, Plus, ArrowRight, Star, Truck, Shield, Zap, Award, Instagram, Play, ChevronRight, ChevronLeft, Calendar, Phone, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template16 = () => {
    const navigate = useNavigate();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    const toggleWishlist = (id) => setWishlist(prev => ({ ...prev, [id]: !prev[id] }));

    // Auto-rotate testimonials (mobile)
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial(prev => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial(prev =>
                diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    // Mobile data
    const mobileCategories = [
        { title: 'Bridal Lehengas', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&h=800&fit=crop', count: '156 designs' },
        { title: 'Wedding Sarees', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&h=800&fit=crop', count: '124 designs' },
        { title: 'Reception Gowns', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=600&h=800&fit=crop', count: '89 designs' },
        { title: 'Sangeet Collection', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=600&h=800&fit=crop', count: '112 designs' },
    ];

    const mobileProducts = [
        { id: 1, name: 'Royal Maroon Lehenga', price: 185000, oldPrice: null, image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=650&fit=crop', badge: 'NEW' },
        { id: 2, name: 'Champagne Gown', price: 95000, oldPrice: 125000, image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=650&fit=crop', badge: 'SALE' },
        { id: 3, name: 'Blush Pink Sangeet', price: 78000, oldPrice: null, image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', badge: null },
        { id: 4, name: 'Emerald Silk Saree', price: 65000, oldPrice: null, image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=650&fit=crop', badge: null },
        { id: 5, name: 'Ivory Pearl Anarkali', price: 42000, oldPrice: 55000, image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=650&fit=crop', badge: 'NEW' },
        { id: 6, name: 'Ruby Velvet Lehenga', price: 165000, oldPrice: null, image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', badge: null },
    ];

    const testimonials = [
        { text: 'Aurora made my wedding truly magical. The bridal lehenga was beyond my dreams!', name: 'Priya Malhotra', location: 'Mumbai', image: 'https://images.unsplash.com/photo-1594744803329-e58b31de8bf5?w=100&h=100&fit=crop' },
        { text: 'The attention to detail and personalized styling was exceptional.', name: 'Ananya Singh', location: 'Delhi', image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop' },
        { text: 'From consultation to the final fitting, every moment was special.', name: 'Kavitha Reddy', location: 'Hyderabad', image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?w=100&h=100&fit=crop' },
    ];

    const features = [
        { icon: Calendar, title: 'Consultation', desc: 'Private bridal styling' },
        { icon: Truck, title: 'Free Shipping', desc: 'Pan India delivery' },
        { icon: Shield, title: 'Authenticity', desc: '100% handcrafted' },
        { icon: Award, title: 'Custom Fit', desc: 'Made to measure' },
    ];

    const instagramImages = [
        'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=300&h=300&fit=crop',
        'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=300&h=300&fit=crop',
    ];

    // Desktop data (from original)
    const heroSlides = [
        { image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1920&h=1080&fit=crop', subtitle: 'Bridal Collection 2025', title: 'Your Dream Wedding', cta: 'View Bridal' },
        { image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=1920&h=1080&fit=crop', subtitle: 'New Season', title: 'Elegant Traditions', cta: 'Shop Collection' },
        { image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=1920&h=1080&fit=crop', subtitle: 'Reception Wear', title: 'Evening Glamour', cta: 'Explore Now' },
    ];

    const desktopCategories = [
        { name: 'Bridal Lehengas', count: 156, image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&h=800&fit=crop' },
        { name: 'Wedding Sarees', count: 124, image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=600&h=800&fit=crop' },
        { name: 'Reception Gowns', count: 89, image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=600&h=800&fit=crop' },
        { name: 'Sangeet Collection', count: 112, image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=600&h=800&fit=crop' },
    ];

    const desktopProducts = [
        { name: 'Royal Maroon Bridal Lehenga', price: 185000, category: 'Bridal', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', isNew: true },
        { name: 'Champagne Reception Gown', price: 95000, category: 'Reception', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=650&fit=crop', isNew: true },
        { name: 'Blush Pink Sangeet Lehenga', price: 78000, category: 'Sangeet', image: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=650&fit=crop', isNew: false },
        { name: 'Emerald Silk Bridal Saree', price: 65000, category: 'Wedding', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=650&fit=crop', isNew: false },
        { name: 'Ivory Pearl Anarkali', price: 42000, category: 'Mehendi', image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=500&h=650&fit=crop', isNew: true },
        { name: 'Ruby Velvet Lehenga', price: 165000, category: 'Bridal', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop', hoverImage: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=500&h=650&fit=crop', isNew: false },
    ];

    const stores = [
        { city: 'Mumbai', address: 'Linking Road, Bandra West', phone: '+91 22 2640 5000' },
        { city: 'Delhi', address: 'DLF Emporio, Vasant Kunj', phone: '+91 11 4603 0000' },
        { city: 'Bangalore', address: 'UB City, Vittal Mallya Road', phone: '+91 80 4150 0000' },
    ];

    const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);

    return (
        <div className="w-full min-h-screen bg-white font-serif">
            {/* === ANNOUNCEMENT BAR === */}
            <div className="bg-rose-50 text-rose-800 text-center py-2 text-[10px] sm:text-xs tracking-wide px-4">
                COMPLIMENTARY BRIDAL CONSULTATION — <span className="underline cursor-pointer">BOOK NOW</span>
            </div>

            {/* === HEADER === */}
            {/* Mobile Header */}
            <header className="lg:hidden bg-white sticky top-0 z-50 border-b border-rose-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-4 sm:px-6 py-4">
                        <button className="min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                            {mobileMenuOpen ? <X className="w-5 h-5 text-rose-900" /> : <Menu className="w-5 h-5 text-rose-900" />}
                        </button>
                        <h1 onClick={() => navigate('/template16')} className="text-2xl font-light tracking-wider text-rose-900 cursor-pointer" style={{ fontFamily: 'serif' }}>
                            AURORA
                        </h1>
                        <div className="flex items-center gap-3">
                            <div className="relative cursor-pointer min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={() => navigate('/template16/cart')}>
                                <ShoppingBag className="w-4 h-4 text-rose-800 hover:text-rose-600 transition-colors" />
                                <span className="absolute top-2 right-1 w-4 h-4 bg-rose-700 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </div>
                        </div>
                    </div>
                </div>
                {mobileMenuOpen && (
                    <div className="bg-white border-t border-rose-100">
                        <nav className="px-6 py-8 space-y-5">
                            <button onClick={() => { navigate('/template16/shop'); setMobileMenuOpen(false); }} className="block text-base text-rose-800 min-h-[44px]">Shop All</button>
                            <button className="block text-base text-rose-800 min-h-[44px]">Bridal</button>
                            <button className="block text-base text-rose-800 min-h-[44px]">Collections</button>
                            <button onClick={() => { navigate('/template16/contact'); setMobileMenuOpen(false); }} className="block text-base text-rose-800 min-h-[44px]">Contact</button>
                            <div className="flex items-center gap-6 pt-4 border-t border-rose-100">
                                <Search className="w-5 h-5 text-rose-800 cursor-pointer" />
                                <Heart className="w-5 h-5 text-rose-800 cursor-pointer" />
                                <User className="w-5 h-5 text-rose-800 cursor-pointer" onClick={() => navigate('/template16/profile')} />
                            </div>
                        </nav>
                    </div>
                )}
            </header>

            {/* Desktop Header - EXACT original */}
            <header className="hidden lg:block bg-white sticky top-0 z-50 border-b border-rose-100">
                <div className="max-w-screen-2xl mx-auto">
                    <div className="flex items-center justify-between px-6 lg:px-12 py-4">
                        <nav className="flex items-center gap-8">
                            <button onClick={() => navigate('/template16/shop')} className="text-sm text-rose-800 hover:text-rose-600 transition-colors tracking-wide">
                                Shop All
                            </button>
                            <button className="text-sm text-rose-800 hover:text-rose-600 transition-colors tracking-wide">
                                Bridal
                            </button>
                            <button className="text-sm text-rose-800 hover:text-rose-600 transition-colors tracking-wide">
                                Collections
                            </button>
                        </nav>
                        <h1 onClick={() => navigate('/template16')} className="text-3xl lg:text-4xl font-light tracking-wider text-rose-900 cursor-pointer" style={{ fontFamily: 'serif' }}>
                            AURORA
                        </h1>
                        <div className="flex items-center gap-5">
                            <Search className="w-4 h-4 text-rose-800 cursor-pointer hover:text-rose-600 transition-colors" />
                            <User className="w-4 h-4 text-rose-800 cursor-pointer hover:text-rose-600 transition-colors" onClick={() => navigate('/template16/profile')} />
                            <Heart className="w-4 h-4 text-rose-800 cursor-pointer hover:text-rose-600 transition-colors" />
                            <div className="relative cursor-pointer" onClick={() => navigate('/template16/cart')}>
                                <ShoppingBag className="w-4 h-4 text-rose-800 hover:text-rose-600 transition-colors" />
                                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-rose-700 text-white text-[10px] rounded-full flex items-center justify-center">0</span>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* === HERO === */}
            {/* Mobile Hero - stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5]">
                    <img src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1920&q=80" alt="Aurora Bridal" className="w-full h-full object-cover" />
                </div>
                <div className="bg-gradient-to-b from-rose-50 to-white px-5 py-8">
                    <p className="text-rose-600 text-xs tracking-[0.3em] mb-3 uppercase">Bridal Collection 2025</p>
                    <h2 className="text-2xl sm:text-3xl font-light text-rose-900 leading-tight mb-4" style={{ fontFamily: 'serif' }}>
                        Your Dream<br/>Wedding
                    </h2>
                    <p className="text-[14px] text-rose-700 leading-relaxed mb-6">Discover exquisite bridal ensembles crafted with love and tradition for your most precious moments.</p>
                    <button onClick={() => navigate('/template16/shop')} className="w-full py-4 bg-white text-rose-900 text-sm tracking-wider border border-rose-200 hover:bg-rose-50 transition-colors min-h-[48px] flex items-center justify-center gap-2">
                        VIEW BRIDAL <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop Hero Slideshow - EXACT original */}
            <section className="hidden lg:block relative h-[90vh]">
                <div className="absolute inset-0">
                    {heroSlides.map((slide, idx) => (
                        <div
                            key={idx}
                            className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === idx ? 'opacity-100' : 'opacity-0'}`}
                        >
                            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-rose-950/50 via-rose-900/30 to-transparent"></div>
                        </div>
                    ))}
                </div>

                <div className="absolute inset-0 flex items-center">
                    <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 w-full">
                        <div className="max-w-xl">
                            <p className="text-rose-200 text-sm tracking-[0.3em] mb-4">{heroSlides[currentSlide].subtitle.toUpperCase()}</p>
                            <h2 className="text-5xl lg:text-7xl font-light text-white mb-6 leading-tight" style={{ fontFamily: 'serif' }}>
                                {heroSlides[currentSlide].title}
                            </h2>
                            <button
                                onClick={() => navigate('/template16/shop')}
                                className="group px-8 py-4 bg-white text-rose-900 text-sm tracking-wider hover:bg-rose-50 transition-colors inline-flex items-center gap-3"
                            >
                                {heroSlides[currentSlide].cta}
                                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Slide Controls */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-6">
                    <button onClick={prevSlide} className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <div className="flex gap-2">
                        {heroSlides.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setCurrentSlide(idx)}
                                className={`h-0.5 transition-all ${currentSlide === idx ? 'w-8 bg-white' : 'w-4 bg-white/50'}`}
                            />
                        ))}
                    </div>
                    <button onClick={nextSlide} className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </div>
            </section>

            {/* === CATEGORIES === */}
            {/* Mobile Categories - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <p className="text-rose-600 text-xs tracking-[0.3em] mb-2 uppercase">Curated For You</p>
                        <h2 className="text-xl sm:text-2xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Shop by Occasion</h2>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {mobileCategories.map((cat, i) => (
                        <div key={i} className="w-[68vw] flex-shrink-0 snap-start cursor-pointer group">
                            <div className="relative rounded-2xl overflow-hidden aspect-[3/4]">
                                <img src={cat.image} alt={cat.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-rose-950/70 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4">
                                    <h3 className="text-white font-medium text-lg mb-0.5">{cat.title}</h3>
                                    <p className="text-rose-200 text-xs">{cat.count}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* Desktop Categories - EXACT original */}
            <section className="hidden lg:block py-20 lg:py-28">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-14">
                        <p className="text-rose-600 text-xs tracking-[0.3em] mb-3">CURATED FOR YOU</p>
                        <h2 className="text-3xl lg:text-4xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Shop by Occasion</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {desktopCategories.map((cat, idx) => (
                            <div key={idx} className="group cursor-pointer">
                                <div className="relative aspect-[3/4] overflow-hidden mb-4">
                                    <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-rose-900/0 group-hover:bg-rose-900/20 transition-colors duration-500"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-rose-950/80 to-transparent">
                                        <h3 className="text-white font-medium text-lg">{cat.name}</h3>
                                        <p className="text-rose-200 text-sm">{cat.count} designs</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === VIDEO LOOKBOOK (Desktop only) - EXACT original === */}
            <section className="hidden lg:block relative h-[70vh] bg-rose-950">
                <img
                    src="https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=1600&h=900&fit=crop"
                    alt="Lookbook"
                    className="w-full h-full object-cover opacity-60"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                        <button className="w-20 h-20 bg-white rounded-full flex items-center justify-center mb-6 mx-auto hover:scale-110 transition-transform shadow-2xl">
                            <Play className="w-8 h-8 text-rose-800 ml-1" />
                        </button>
                        <p className="text-rose-200 text-xs tracking-[0.3em] mb-3">WATCH THE FILM</p>
                        <h2 className="text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: 'serif' }}>The Bridal Story</h2>
                    </div>
                </div>
            </section>

            {/* === PRODUCTS === */}
            {/* Mobile Products - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-rose-50/30">
                <div className="px-5 mb-8">
                    <div className="flex items-end justify-between">
                        <div>
                            <p className="text-rose-600 text-xs tracking-[0.3em] mb-2 uppercase">New Arrivals</p>
                            <h2 className="text-xl sm:text-2xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Bridal Favourites</h2>
                        </div>
                    </div>
                </div>
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                    {mobileProducts.map((p) => (
                        <div key={p.id} className="w-[44vw] flex-shrink-0 snap-start">
                            <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-rose-100 mb-2.5">
                                <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                                {p.badge && <span className="absolute top-2 left-2 bg-rose-700 text-white text-[10px] px-2.5 py-0.5">{p.badge}</span>}
                                <button onClick={() => toggleWishlist(p.id)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                    <Heart className={`w-3.5 h-3.5 ${wishlist[p.id] ? 'fill-rose-700 text-rose-700' : 'text-rose-500'}`} />
                                </button>
                                <button className="absolute bottom-2 right-2 w-8 h-8 bg-rose-700 rounded-full flex items-center justify-center">
                                    <Plus className="w-3.5 h-3.5 text-white" />
                                </button>
                            </div>
                            <h3 className="text-xs text-rose-900 line-clamp-1 mb-0.5">{p.name}</h3>
                            <div className="flex items-center gap-1.5">
                                <span className="text-xs text-rose-700">{'\u20B9'}{p.price.toLocaleString()}</span>
                                {p.oldPrice && <span className="text-[10px] text-rose-400 line-through">{'\u20B9'}{p.oldPrice.toLocaleString()}</span>}
                            </div>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
                <div className="text-center mt-8 px-5">
                    <button onClick={() => navigate('/template16/shop')} className="text-sm text-rose-800 border-b border-rose-400 pb-1 min-h-[44px] inline-flex items-center">
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop Products - EXACT original */}
            <section className="hidden lg:block py-20 lg:py-28 bg-rose-50/30">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
                    <div className="flex items-end justify-between mb-14">
                        <div>
                            <p className="text-rose-600 text-xs tracking-[0.3em] mb-3">NEW ARRIVALS</p>
                            <h2 className="text-3xl lg:text-4xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Bridal Favourites</h2>
                        </div>
                        <button onClick={() => navigate('/template16/shop')} className="flex items-center gap-2 text-sm text-rose-800 hover:text-rose-600 transition-colors">
                            View All <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {desktopProducts.map((product, idx) => (
                            <div
                                key={idx}
                                className="group"
                                onMouseEnter={() => setHoveredProduct(idx)}
                                onMouseLeave={() => setHoveredProduct(null)}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden mb-4 bg-rose-100">
                                    <img
                                        src={hoveredProduct === idx ? product.hoverImage : product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover transition-all duration-500"
                                    />
                                    {product.isNew && (
                                        <span className="absolute top-4 left-4 bg-rose-700 text-white text-xs px-3 py-1">NEW</span>
                                    )}
                                    <button className="absolute top-4 right-4 w-9 h-9 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                                        <Heart className="w-4 h-4 text-rose-700" />
                                    </button>
                                    <button className="absolute bottom-4 left-4 right-4 py-3 bg-white text-rose-900 text-sm tracking-wider opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                        Quick View
                                    </button>
                                </div>
                                <div className="text-center">
                                    <p className="text-[11px] text-rose-500 tracking-wider mb-1">{product.category.toUpperCase()}</p>
                                    <h3 className="text-rose-900 font-medium mb-1">{product.name}</h3>
                                    <p className="text-rose-700">{'\u20B9'}{product.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === BRIDAL CONSULTATION (Desktop only) - EXACT original === */}
            <section className="hidden lg:block py-20 lg:py-28 bg-white">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=800&h=600&fit=crop"
                                alt="Bridal Consultation"
                                className="w-full aspect-[4/3] object-cover"
                            />
                        </div>
                        <div className="lg:pl-8">
                            <p className="text-rose-600 text-xs tracking-[0.3em] mb-4">THE AURORA EXPERIENCE</p>
                            <h2 className="text-3xl lg:text-4xl font-light text-rose-900 mb-6" style={{ fontFamily: 'serif' }}>
                                Personal Bridal Consultation
                            </h2>
                            <p className="text-rose-700 leading-relaxed mb-6">
                                Begin your bridal journey with a private consultation. Our expert stylists will guide you
                                through our collection, understanding your vision to find the perfect ensemble for your special day.
                            </p>
                            <ul className="space-y-3 mb-8">
                                <li className="flex items-center gap-3 text-rose-700">
                                    <span className="w-2 h-2 bg-rose-400 rounded-full"></span>
                                    Private viewing of exclusive collections
                                </li>
                                <li className="flex items-center gap-3 text-rose-700">
                                    <span className="w-2 h-2 bg-rose-400 rounded-full"></span>
                                    Personalized styling recommendations
                                </li>
                                <li className="flex items-center gap-3 text-rose-700">
                                    <span className="w-2 h-2 bg-rose-400 rounded-full"></span>
                                    Custom alterations and fittings
                                </li>
                            </ul>
                            <button
                                onClick={() => navigate('/template16/contact')}
                                className="group px-8 py-4 bg-rose-800 text-white text-sm tracking-wider hover:bg-rose-900 transition-colors inline-flex items-center gap-3"
                            >
                                <Calendar className="w-4 h-4" />
                                Book Appointment
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* === BRAND STORY (Mobile only) === */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/3]">
                    <img src="https://images.unsplash.com/photo-1583391733956-3750e0ff4e8b?w=1920&q=80" alt="The Aurora Experience" className="w-full h-full object-cover" />
                </div>
                <div className="px-5 py-8 bg-gradient-to-b from-rose-50 to-white">
                    <p className="text-rose-600 text-xs tracking-[0.3em] mb-2 uppercase">The Aurora Experience</p>
                    <h2 className="text-xl font-light text-rose-900 mb-4" style={{ fontFamily: 'serif' }}>Personal Bridal Consultation</h2>
                    <p className="text-[14px] text-rose-700 leading-relaxed mb-5">Begin your bridal journey with a private consultation. Our expert stylists will guide you through our collection to find the perfect ensemble for your special day.</p>
                    <button onClick={() => navigate('/template16/contact')} className="inline-flex items-center gap-2 px-6 py-3 bg-rose-800 text-white text-sm tracking-wider hover:bg-rose-900 transition-colors min-h-[44px]">
                        <Calendar className="w-4 h-4" /> Book Appointment
                    </button>
                </div>
            </section>

            {/* === FEATURES === */}
            {/* Mobile Features - horizontal scroll */}
            <section className="lg:hidden py-10 sm:py-16 bg-rose-50">
                <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide">
                    {features.map((f, i) => (
                        <div key={i} className="min-w-[150px] flex-shrink-0 snap-start text-center py-5 px-4 bg-white rounded-xl">
                            <div className="w-11 h-11 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2.5">
                                <f.icon className="w-5 h-5 text-rose-700" />
                            </div>
                            <h3 className="text-xs font-medium text-rose-900 mb-0.5">{f.title}</h3>
                            <p className="text-[10px] text-rose-600">{f.desc}</p>
                        </div>
                    ))}
                    <div className="w-4 flex-shrink-0" />
                </div>
            </section>

            {/* === TESTIMONIALS === */}
            {/* Mobile Testimonials - swipeable */}
            <section className="lg:hidden py-10 sm:py-16 bg-white">
                <div className="px-5 mb-8">
                    <div className="text-center">
                        <p className="text-rose-600 text-xs tracking-[0.3em] mb-2 uppercase">Real Brides</p>
                        <h2 className="text-xl sm:text-2xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Love Stories</h2>
                    </div>
                </div>
                <div className="overflow-hidden px-5" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                    <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                        {testimonials.map((t, i) => (
                            <div key={i} className="w-full flex-shrink-0 px-2">
                                <div className="bg-rose-50 rounded-xl p-6 text-center">
                                    <div className="flex justify-center gap-1 mb-3">
                                        {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-rose-400 text-rose-400" />)}
                                    </div>
                                    <p className="text-[14px] text-rose-800 leading-relaxed mb-4 italic">"{t.text}"</p>
                                    <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full mx-auto mb-2 object-cover" />
                                    <p className="text-sm font-medium text-rose-900">{t.name}</p>
                                    <p className="text-xs text-rose-600">{t.location}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex justify-center gap-2 mt-5">
                        {testimonials.map((_, i) => (
                            <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-2 rounded-full transition-all ${activeTestimonial === i ? 'w-6 bg-rose-700' : 'w-2 bg-rose-300'}`} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop Testimonials - EXACT original */}
            <section className="hidden lg:block py-20 lg:py-28 bg-rose-50">
                <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-14">
                        <p className="text-rose-600 text-xs tracking-[0.3em] mb-3">REAL BRIDES</p>
                        <h2 className="text-3xl lg:text-4xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Love Stories</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {testimonials.map((item, idx) => (
                            <div key={idx} className="bg-white p-8 text-center">
                                <div className="flex justify-center gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <Star key={i} className="w-4 h-4 fill-rose-400 text-rose-400" />
                                    ))}
                                </div>
                                <p className="text-rose-800 italic mb-6 leading-relaxed">"{item.text}"</p>
                                <img src={item.image} alt={item.name} className="w-14 h-14 rounded-full mx-auto mb-3 object-cover" />
                                <p className="font-medium text-rose-900">{item.name}</p>
                                <p className="text-rose-600 text-sm">{item.location}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === STORE LOCATIONS (Desktop only) - EXACT original === */}
            <section className="hidden lg:block py-20 lg:py-28 bg-white">
                <div className="max-w-screen-xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-14">
                        <p className="text-rose-600 text-xs tracking-[0.3em] mb-3">VISIT US</p>
                        <h2 className="text-3xl lg:text-4xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Our Boutiques</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {stores.map((store, idx) => (
                            <div key={idx} className="text-center p-6 border border-rose-200 hover:border-rose-400 transition-colors">
                                <MapPin className="w-6 h-6 text-rose-600 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-rose-900 mb-2">{store.city}</h3>
                                <p className="text-rose-600 text-sm mb-3">{store.address}</p>
                                <p className="flex items-center justify-center gap-2 text-rose-700 text-sm">
                                    <Phone className="w-3 h-3" />
                                    {store.phone}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === NEWSLETTER === */}
            {/* Mobile Newsletter - stacked rounded */}
            <section className="lg:hidden py-10 sm:py-16 bg-rose-100">
                <div className="max-w-xl mx-auto px-5 text-center">
                    <p className="text-rose-600 text-xs tracking-[0.3em] mb-2 uppercase">Stay Inspired</p>
                    <h3 className="text-xl sm:text-2xl font-light text-rose-900 mb-3" style={{ fontFamily: 'serif' }}>Join the Aurora Circle</h3>
                    <p className="text-[14px] text-rose-700 mb-6">Be the first to discover new collections and exclusive bridal events.</p>
                    <div className="flex flex-col gap-3">
                        <input type="email" placeholder="Enter your email" className="flex-1 px-5 py-4 bg-white border-0 text-rose-900 text-sm placeholder-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300 rounded-xl min-h-[48px]" />
                        <button className="px-8 py-4 bg-rose-800 text-white text-sm tracking-wider hover:bg-rose-900 transition-colors rounded-xl min-h-[48px]">Subscribe</button>
                    </div>
                </div>
            </section>

            {/* Desktop Newsletter - EXACT original */}
            <section className="hidden lg:block py-20 bg-rose-100">
                <div className="max-w-xl mx-auto px-6 text-center">
                    <p className="text-rose-600 text-xs tracking-[0.3em] mb-3">STAY INSPIRED</p>
                    <h2 className="text-2xl lg:text-3xl font-light text-rose-900 mb-4" style={{ fontFamily: 'serif' }}>Join the Aurora Circle</h2>
                    <p className="text-rose-700 text-sm mb-8">
                        Be the first to discover new collections and exclusive bridal events.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-5 py-4 bg-white border-0 text-rose-900 text-sm placeholder-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-300"
                        />
                        <button className="px-8 py-4 bg-rose-800 text-white text-sm tracking-wider hover:bg-rose-900 transition-colors">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* === INSTAGRAM - shared with responsive classes === */}
            <section className="py-10 sm:py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-5 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12">
                        <div className="flex items-center justify-center gap-2 mb-2">
                            <Instagram className="w-5 h-5 text-rose-600" />
                            <span className="text-rose-600 font-medium text-sm">@aurora_bridal</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl lg:text-3xl font-light text-rose-900" style={{ fontFamily: 'serif' }}>Follow The Journey</h2>
                    </div>
                    <div className="grid grid-cols-3 lg:grid-cols-6 gap-1.5 sm:gap-2 lg:gap-3">
                        {instagramImages.map((img, i) => (
                            <div key={i} className="group relative aspect-square overflow-hidden rounded-lg cursor-pointer">
                                <img src={img} alt={`Instagram ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-rose-900/0 group-hover:bg-rose-900/30 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* === FOOTER === */}
            {/* Mobile Footer */}
            <footer className="lg:hidden bg-rose-900 text-white">
                <div className="px-4 sm:px-6 py-10 sm:py-12">
                    <div className="grid grid-cols-2 gap-6 sm:gap-8">
                        <div className="col-span-2">
                            <h3 className="text-2xl font-light tracking-wide mb-4" style={{ fontFamily: 'serif' }}>AURORA</h3>
                            <p className="text-rose-300 text-sm leading-relaxed">Crafting dreams for your most precious moments.</p>
                        </div>
                        <div>
                            <h4 className="text-xs tracking-widest mb-4 text-rose-200">SHOP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template16/shop')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">All Collections</span></li>
                                <li><span className="text-rose-300">Bridal</span></li>
                                <li><span className="text-rose-300">Reception</span></li>
                                <li><span className="text-rose-300">Sangeet</span></li>
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-xs tracking-widest mb-4 text-rose-200">HELP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template16/contact')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">Contact Us</span></li>
                                <li><span onClick={() => navigate('/template16/orders')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-rose-300">Shipping</span></li>
                                <li><span className="text-rose-300">Size Guide</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-rose-800 mt-8 pt-6">
                        <p className="text-rose-400 text-xs text-center">&copy; 2025 Aurora Bridal. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Desktop Footer - EXACT original */}
            <footer className="hidden lg:block bg-rose-900 text-white">
                <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-10 lg:gap-12">
                        <div>
                            <h3 className="text-2xl font-light tracking-wide mb-6" style={{ fontFamily: 'serif' }}>AURORA</h3>
                            <p className="text-rose-300 text-sm leading-relaxed">
                                Crafting dreams for your most precious moments. Where tradition meets elegance.
                            </p>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-rose-200">SHOP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template16/shop')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">All Collections</span></li>
                                <li><span className="text-rose-300 hover:text-white cursor-pointer transition-colors">Bridal</span></li>
                                <li><span className="text-rose-300 hover:text-white cursor-pointer transition-colors">Reception</span></li>
                                <li><span className="text-rose-300 hover:text-white cursor-pointer transition-colors">Sangeet</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-rose-200">HELP</h4>
                            <ul className="space-y-3 text-sm">
                                <li><span onClick={() => navigate('/template16/contact')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">Contact Us</span></li>
                                <li><span onClick={() => navigate('/template16/orders')} className="text-rose-300 hover:text-white cursor-pointer transition-colors">Track Order</span></li>
                                <li><span className="text-rose-300 hover:text-white cursor-pointer transition-colors">Shipping</span></li>
                                <li><span className="text-rose-300 hover:text-white cursor-pointer transition-colors">Size Guide</span></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="text-xs tracking-widest mb-5 text-rose-200">CONNECT</h4>
                            <p className="text-rose-300 text-sm">bride@aurora.in</p>
                            <p className="text-rose-300 text-sm mt-2">+91 98765 43210</p>
                            <p className="text-rose-300 text-sm mt-4">Mon - Sat: 10am - 8pm</p>
                        </div>
                    </div>

                    <div className="border-t border-rose-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-rose-400 text-xs">&copy; 2025 Aurora Bridal. All rights reserved.</p>
                        <div className="flex gap-6 text-xs text-rose-400">
                            <span className="hover:text-white cursor-pointer transition-colors">Privacy</span>
                            <span className="hover:text-white cursor-pointer transition-colors">Terms</span>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Template16;